import { CampaignVideoQuizResults } from '@/api/campaigns/campagin-quiz-results'
import { useEducationModuleById } from '@/api/education-modules/module-by-id'
import collectingSpaceship from '@/assets/images/collecting-spaceship.svg'
import noQuizImage from '@/assets/images/sofa-tv.svg'
import CardHeader from '@/common/components/Card/CardHeader'
import { theme } from '@/theme/theme'
import { faCommentCheck, faCommentQuestion } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Card, Grid, styled, Typography, Tooltip } from '@mui/material'
import { FC } from 'react'
import { Trans, useTranslation } from 'react-i18next'

type CampaignQuizResultsParams = {
  videoId: string
  campaignScore: number
  quizResults: CampaignVideoQuizResults
  hasQuizzes: boolean
}

const getScoreColor = (score: number) => {
  if (score <= 50) return theme.palette.pink[800]
  return theme.palette.green[500]
}

const VideoQuizResults: FC<CampaignQuizResultsParams> = ({ videoId, campaignScore, quizResults, hasQuizzes }) => {
  const { t } = useTranslation()
  const { data: video } = useEducationModuleById(videoId)

  const truncateText = (text: string) => {
    if (text.length <= 10) return text
    if (text.length <= 70) return text

    // Find the last space before the 70 character limit
    const truncated = text.substring(0, 70)
    const lastSpaceIndex = truncated.lastIndexOf(' ')

    if (lastSpaceIndex > 0) {
      return truncated.substring(0, lastSpaceIndex) + '...'
    }

    return truncated + '...'
  }

  const shouldShowTooltip = (text: string) => {
    return text.length > 10 && text.length > 70
  }

  return (
    <Card>
      <CardHeader title={t('campaignDashboard.campaignQuizResults.title')}>
        {hasQuizzes &&
          ((video?.number_of_quiz_questions && video?.number_of_quiz_questions > 0) ||
            (video?.quizzes && video?.quizzes.length > 0)) && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.open(`/content/quiz/preview/${video?.quizzes?.[0]?._id}`, '_blank')}>
              {t('campaignDashboard.campaignQuizResults.viewQuiz')}
            </Button>
          )}
      </CardHeader>
      <Typography fontSize={12} paddingTop={1}>
        {t('campaignDashboard.campaignQuizResults.subTitle')}
      </Typography>
      {hasQuizzes &&
      ((video?.number_of_quiz_questions && video?.number_of_quiz_questions > 0) ||
        (video?.quizzes && video?.quizzes.length > 0)) ? (
        !!campaignScore && campaignScore >= 0 ? (
          <Box key={video._id} display="flex" justifyContent="space-between" gap={6}>
            <Box flex={1}>
              {quizResults?.map((question, i) => {
                return (
                  <Grid key={i} container paddingTop={4} spacing={6}>
                    <Grid item xs={3}>
                      <Box display="flex" flexDirection="column" justifyContent="center" paddingTop={1}>
                        <StyledProgressBar>
                          <StyledProgressBarFill score={question.score} />
                        </StyledProgressBar>
                        <Box display="flex" gap={2} alignItems="center">
                          <Typography fontSize={20} fontWeight={theme.typography.fontWeightSemiBold}>
                            {question.score >= 0 && question.score + '%'}
                          </Typography>
                          <Typography>
                            {'(' + question.correct_answered + '/' + question.total_answered + ')'}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={9}>
                      <Box display="flex" gap={1} alignItems="center" paddingBottom={1}>
                        <FontAwesomeIcon icon={faCommentQuestion}></FontAwesomeIcon>
                        {shouldShowTooltip(question.question) ? (
                          <Tooltip title={question.question} arrow>
                            <span>
                              <Typography>{truncateText(question.question)}</Typography>
                            </span>
                          </Tooltip>
                        ) : (
                          <Typography>{truncateText(question.question)}</Typography>
                        )}
                      </Box>
                      <Box display="flex" gap={1} alignItems="center">
                        <FontAwesomeIcon icon={faCommentCheck} color={theme.palette.green[900]}></FontAwesomeIcon>
                        {shouldShowTooltip(question.correct_answer) ? (
                          <Tooltip title={question.correct_answer} arrow>
                            <span>
                              <Typography fontWeight={theme.typography.fontWeightSemiBold}>
                                {truncateText(question.correct_answer)}
                              </Typography>
                            </span>
                          </Tooltip>
                        ) : (
                          <Typography fontWeight={theme.typography.fontWeightSemiBold}>
                            {truncateText(question.correct_answer)}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                )
              })}
            </Box>
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} paddingTop={3}>
              <Typography fontWeight={theme.typography.fontWeightBold}>
                {t('campaignDashboard.campaignQuizResults.averageQuizScore')}
              </Typography>
              <StyledAverageQuizScoreBox score={campaignScore || 0}>
                <Typography fontWeight={theme.typography.fontWeightBold} fontSize={80}>
                  {campaignScore.toFixed()}
                </Typography>
              </StyledAverageQuizScoreBox>
            </Box>
          </Box>
        ) : (
          <StyledNoResultsContainer key={video._id}>
            <StyledNoScoreImage />
            <Typography fontSize={18}>{t('campaignDashboard.campaignQuizResults.noInteractions')}</Typography>
          </StyledNoResultsContainer>
        )
      ) : (
        <StyledNoResultsContainer>
          <StyledNoQuizImage />
          <Typography fontSize={18}>
            <Trans
              i18nKey={'campaignDashboard.campaignQuizResults.noQuiz'}
              components={{
                1: <b />,
              }}
            />
          </Typography>
        </StyledNoResultsContainer>
      )}
    </Card>
  )
}

const StyledProgressBar = styled(Box)(() => ({
  width: '100%',
  background: theme.palette.grey[300],
  height: theme.spacing(1),
  borderRadius: 10,
  marginBottom: theme.spacing(1),
}))

const StyledAverageQuizScoreBox = styled(Box)(({ score }: { score: number }) => ({
  minWidth: theme.spacing(21),
  padding: theme.spacing(6, 4),
  border: '2px solid ' + getScoreColor(score),
  borderRadius: '10px',
  display: 'flex',
  justifyContent: 'center',
  alignContent: 'center',
}))

const StyledProgressBarFill = styled(Box)(({ score }: { score: number }) => ({
  width: (score > 0 ? score : 0) + '%',
  background: getScoreColor(score),
  height: theme.spacing(1),
  borderRadius: 10,
}))

const StyledNoScoreImage = styled(Box)(() => ({
  backgroundImage: `url(${collectingSpaceship})`,
  backgroundSize: '190px',
  backgroundRepeat: 'no-repeat',
  width: '190px',
  height: '200px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
}))

const StyledNoQuizImage = styled(Box)(() => ({
  backgroundImage: `url(${noQuizImage})`,
  backgroundSize: '300px',
  backgroundRepeat: 'no-repeat',
  width: '300px',
  height: '170px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
}))

const StyledNoResultsContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1),
  gap: theme.spacing(3),
}))
export default VideoQuizResults
